import { isMediaElementTarget } from "./keyboardTarget";

/**
 * Height (px) of the strip at the bottom of a media element that its native
 * control bar occupies: iOS Safari's inline controls are ~44px tall and
 * Android Chrome's ~48px, plus a little slop for the touch target.
 *
 * A band rather than a hit-test because the native controls live in the
 * element's shadow DOM: every touch on them reports the `<video>` itself as its
 * target, so where the finger landed is the only thing left to go on.
 */
export const MEDIA_CONTROLS_BAND_PX = 56;

/**
 * True when a touch on a media element landed on its native control bar, which
 * owns the gesture (seeking by dragging the scrub bar, play/pause, volume,
 * fullscreen).
 *
 * A touch anywhere else on the player is *not* the player's: over the picture, a
 * horizontal drag is the swipe a phone user expects to page a gallery with, and
 * on a phone the picture is most of the screen. An audio player is nothing but
 * its control bar (and no taller than the band), so every touch on one belongs
 * to it, and an element rendered without native controls has no bar to protect.
 */
export const isMediaControlsTouch = (
  target: EventTarget | null,
  clientY: number
): boolean => {
  if (!isMediaElementTarget(target)) return false;
  const media = target as HTMLMediaElement;
  if (!media.controls) return false;
  const rect = media.getBoundingClientRect();
  const band = Math.min(MEDIA_CONTROLS_BAND_PX, rect.height);
  return clientY >= rect.bottom - band;
};
